import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useProfile } from '@/hooks/useProfile'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { useMyPartiesWithDetails } from '@/hooks/useMyPartiesWithDetails'
import { AppLayout } from '@/components/layout/AppLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export default function Home() {
  const { user } = useAuth()
  const { profile, isLoading: profileLoading } = useProfile()
  const { isAdmin } = useIsAdmin()
  const { parties, isLoading: partiesLoading } = useMyPartiesWithDetails()

  const displayName = profile?.username || user?.email?.split('@')[0] || 'User'
  const hasParties = parties && parties.length > 0

  return (
    <AppLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold">Welcome{!profileLoading && `, ${displayName}`}!</h1>
          <p className="text-muted-foreground mt-2">
            A browser-based multiplayer political simulation game.
          </p>
        </div>

        {isAdmin && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="py-4">
              <p className="text-sm text-blue-800">
                You have <strong>admin access</strong>. You can create and manage worlds, nations,
                regions, and districts.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Your Parties Section */}
        {!partiesLoading && hasParties && (
          <div>
            <h2 className="mb-4 text-xl font-semibold">Your Parties</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {parties.map((party) => (
                <Card key={party.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: party.color }}
                      />
                      <CardTitle className="text-base">
                        {party.name} ({party.abbreviation})
                      </CardTitle>
                    </div>
                    <CardDescription>
                      {party.nation.name} &bull; {party.world.name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-3 flex gap-4 text-sm text-muted-foreground">
                      <span>💰 {party.money.toLocaleString()}</span>
                      <span>⚡ {party.action_points} AP</span>
                      <span>📊 {party.prestige}</span>
                    </div>
                    <Button asChild variant="outline" className="w-full">
                      <Link to={`/parties/${party.id}`}>Go to Party</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-4">
              <Button asChild variant="outline">
                <Link to="/join">Join Another Nation</Link>
              </Button>
            </div>
          </div>
        )}

        {/* No Parties CTA */}
        {!partiesLoading && !hasParties && (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center">
              <h3 className="text-lg font-medium">You haven't joined any nations yet.</h3>
              <p className="text-muted-foreground mt-1">
                Create a political party and compete for power!
              </p>
              <Button asChild className="mt-4">
                <Link to="/join">Join a Nation</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Loading state for parties */}
        {partiesLoading && (
          <div>
            <div className="bg-muted mb-4 h-6 w-32 animate-pulse rounded" />
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2].map((i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <div className="bg-muted h-5 w-40 animate-pulse rounded" />
                    <div className="bg-muted mt-2 h-4 w-32 animate-pulse rounded" />
                  </CardHeader>
                  <CardContent>
                    <div className="bg-muted mb-3 h-4 w-48 animate-pulse rounded" />
                    <div className="bg-muted h-9 w-full animate-pulse rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle>Explore Worlds</CardTitle>
              <CardDescription>
                Browse available game worlds and join one to start playing.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <Link to="/worlds">View Worlds</Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Your Profile</CardTitle>
              <CardDescription>View and manage your account settings.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm">
                <span className="text-muted-foreground">Email:</span> {user?.email}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Username:</span>{' '}
                {profile?.username || 'Not set'}
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Role:</span> {profile?.role || 'Loading...'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Getting Started</CardTitle>
              <CardDescription>New to Living Polity?</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="text-muted-foreground list-inside list-disc space-y-1 text-sm">
                <li>Join a nation to participate</li>
                <li>Create a political party</li>
                <li>Campaign for elections</li>
                <li>Pass legislation and govern</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  )
}
